import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  Auth,
  FacebookAuthProvider,
  User as FirebaseUser,
  GoogleAuthProvider,
  getRedirectResult,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithRedirect,
  signOut,
  updateEmail,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject, filter, firstValueFrom, take } from 'rxjs';

import { showError } from '../../interceptors/error.interceptor';
import { isApiError } from '../error/error';
import { Language, storeLanguage } from '../i18n/i18n';
import { routes } from '../routes/routes';
import { BaseService } from '../service/base.service';
import { LoginProvider } from './dto/login.dto';
import { UpdateUserDto } from './dto/update-user.dto';
import { UserDto } from './dto/user.dto';
import { User as SwipesUser, createUser } from './user';

const emailForSignInKey = 'emailForSignIn';

@Injectable({ providedIn: 'root' })
export class AuthService extends BaseService {
  auth = inject(Auth);

  currentUser = new ReplaySubject<SwipesUser | null>(1);
  currentUser$ = this.currentUser.asObservable();

  constructor(
    private translateService: TranslateService,
    private router: Router,
    private http: HttpClient,
  ) {
    super();

    this.currentUser$.subscribe(user => {
      console.debug('Logged in user: ', user);
      if (user?.authenticated === 'fully') {
        this.useLanguage(user.language || 'de');
      }
    });

    this.auth.onAuthStateChanged(async firebaseUser => {
      if (!firebaseUser) {
        this.currentUser.next(null);
        return;
      }

      try {
        const user = await this.getSwipesUser(firebaseUser);
        this.currentUser.next(user);
      } catch (e) {
        await this.logout();
      }
    });

    // Forward active language to Firebase Auth - important for correctly localized mails.
    this.auth.languageCode = this.translateService.currentLang;
    this.translateService.onLangChange.subscribe(event => {
      this.auth.languageCode = event.lang;
    });
  }

  async login(provider: LoginProvider): Promise<void> {
    if (provider.type === 'email') {
      const continueUrl = `${window.location.origin}${routes.emailLinkLogin.url()}${window.location.search}`;
      await this.sendSignInLinkToEmail(provider.email, continueUrl);
    } else if (provider.type === 'google') {
      await signInWithRedirect(this.auth, new GoogleAuthProvider());
    } else if (provider.type === 'facebook') {
      await signInWithRedirect(this.auth, new FacebookAuthProvider());
    } else if (provider.type === 'password') {
      await signInWithEmailAndPassword(this.auth, provider.email, provider.password);
    }
  }

  async loginCustomToken(token: string): Promise<void> {
    await signInWithCustomToken(this.auth, token);
  }

  async sendSignInLinkToEmail(email: string, url: string): Promise<void> {
    await sendSignInLinkToEmail(this.auth, email, { url, handleCodeInApp: true });
    localStorage.setItem(emailForSignInKey, email);
  }

  async signInWithEmailLink(link: string, email: string): Promise<void> {
    if (!this.isValidSignInWithEmailLink(link)) {
      return;
    }

    await signInWithEmailLink(this.auth, email, link);
    localStorage.removeItem(emailForSignInKey);
  }

  getStoredEmailForSignIn(): string | null {
    return localStorage.getItem(emailForSignInKey);
  }

  async handleRedirectResultFromExternalProvider() {
    await getRedirectResult(this.auth);
  }

  async logout() {
    await signOut(this.auth);
    await this.waitForLogout();
    await this.router.navigateByUrl(routes.login.url());
  }

  async refreshCurrentUser() {
    const user = await this.getSwipesUser(this.auth.currentUser!);

    this.currentUser.next(user);
  }

  async changeEmail(email: string) {
    await updateEmail(this.auth.currentUser!, email);
  }

  async updateUser(dto: UpdateUserDto): Promise<UserDto> {
    const userDto = await firstValueFrom(this.http.put<UserDto>(`${this.apiUrl}/accounts/me`, dto));

    this.currentUser.next(createUser(this.auth.currentUser!, userDto));

    return userDto;
  }

  getCurrentUser(): Promise<SwipesUser | null> {
    return firstValueFrom(this.currentUser$);
  }

  isValidSignInWithEmailLink(link: string): boolean {
    return isSignInWithEmailLink(this.auth, link);
  }

  private async waitForLogout(): Promise<void> {
    await firstValueFrom(
      this.currentUser$.pipe(
        filter(u => !u),
        take(1),
      ),
    );

    return;
  }

  private useLanguage(language: Language) {
    this.translateService.use(language);
    storeLanguage(language);
  }

  private async getSwipesUser(user: FirebaseUser): Promise<SwipesUser> {
    try {
      const userDto = await firstValueFrom(
        this.http.get<UserDto>(`${this.apiUrl}/current_user`, {
          context: new HttpContext().set(showError, false),
        }),
      );
      return createUser(user, userDto);
    } catch (e) {
      if (isApiError(e) && e.httpError.status === 403) {
        // A 403 means that the user does not exist in backend -> we continue Firebase authenticated only and start the onboarding.
        return createUser(user, null);
      } else {
        // Any other error is unexpected -> rethrow.
        throw e;
      }
    }
  }
}
